import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Send, Trash2, Maximize2, X, Zap, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { apiSendAssistantMessage, getTenantSlug, type AssistantMessage as ApiMessage } from "@/lib/api";
import {
  AssistantMessage as MessageBubble,
  TypingIndicator,
  type Message,
  type UserAvatarInfo,
} from "@/components/ai/AssistantMessage";

const GENERAL_SUGGESTIONS = [
  { label: "Current headcount", prompt: "What is our current total headcount?" },
  { label: "Payroll summary",   prompt: "Show me the payroll summary for the last 3 months." },
  { label: "Active employees",  prompt: "List all active employees." },
  { label: "HR overview",       prompt: "Give me a quick HR overview: headcount, new hires this month, and turnover trend." },
];

type AssistantContext = "general" | "onboarding";

interface OnboardingContext {
  role?: string;
  department?: string;
  buddyName?: string;
  progress?: number;
  totalTasks?: number;
  completedTasks?: number;
  pendingTasks?: number;
  nextTaskTitle?: string;
  nextTaskDescription?: string;
}

interface SaraOpenEventDetail {
  context?: AssistantContext;
  onboarding?: OnboardingContext;
}

function createSessionId() {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getStoredSessionId(storageKey: string) {
  try {
    const stored = localStorage.getItem(`${storageKey}_sid`);
    if (stored) return stored;
    const id = createSessionId();
    localStorage.setItem(`${storageKey}_sid`, id);
    return id;
  } catch {
    return createSessionId();
  }
}

function getStoredMessages(storageKey: string): Message[] {
  try {
    const raw = localStorage.getItem(`${storageKey}_msgs`);
    if (raw) return JSON.parse(raw) as Message[];
  } catch {}
  return [];
}

function buildOnboardingPrompt(question: string, context: OnboardingContext | null) {
  const details = [
    context?.role ? `Позиција: ${context.role}` : null,
    context?.department ? `Оддел: ${context.department}` : null,
    context?.buddyName ? `Онбординг другар/ментор: ${context.buddyName}` : null,
    typeof context?.progress === "number" ? `Прогрес: ${context.progress}%` : null,
    typeof context?.pendingTasks === "number" ? `Преостанати задачи: ${context.pendingTasks}` : null,
    context?.nextTaskTitle ? `Следна задача: ${context.nextTaskTitle}` : null,
  ].filter(Boolean);

  if (!details.length) return question;
  return `Јас сум на страницата Мој Онбординг.\n${details.join("\n")}\n\n${question}`;
}

function getOnboardingSuggestions(t: TFunction, context: OnboardingContext | null) {
  const base = [
    t("onboarding.saraPromptNextStep", "Кој е мојот следен чекор?"),
    t("onboarding.saraPromptTasks", "Кои задачи треба да ги завршам?"),
    t("onboarding.saraPromptBuddy", "Кој е мојот ментор/другар?"),
    t("onboarding.saraPromptPrepare", "Што треба да подготвам прво?"),
  ];

  const department = (context?.department || "").toLowerCase();
  let departmentPrompts: string[] = [];

  if (department.includes("hr") || department.includes("admin") || department.includes("човечки")) {
    departmentPrompts = [
      t("onboarding.saraPromptHrPolicies", "Кои HR политики треба да ги прочитам?"),
      t("onboarding.saraPromptTodayTasks", "Кои onboarding задачи имам денес?"),
    ];
  } else if (department.includes("production") || department.includes("operations") || department.includes("опера") || department.includes("производ")) {
    departmentPrompts = [
      t("onboarding.saraPromptTrainings", "Кои обуки треба да ги завршам?"),
      t("onboarding.saraPromptSafety", "Дали имам безбедносни инструкции?"),
    ];
  } else if (department.includes("finance") || department.includes("финанс")) {
    departmentPrompts = [
      t("onboarding.saraPromptDocuments", "Кои документи треба да ги доставам?"),
      t("onboarding.saraPromptSystems", "Кои системи треба да ги користам?"),
    ];
  }

  return [...departmentPrompts, ...base]
    .slice(0, 4)
    .map((label) => ({ label, prompt: buildOnboardingPrompt(label, context) }));
}

function expandCommand(text: string): string {
  const t = text.trim();
  if (t === "/onboarding") return "I want to create an onboarding session for an employee. Can you first show me the available templates and list some employees I can choose from?";
  if (t === "/survey")     return "I want to create a new employee survey. What kind of survey should it be?";
  if (t === "/report")     return "I want to generate an HR report. What data do you need: headcount, payroll, leave trends, turnover, or department comparison?";
  if (t === "/workflow")   return "I want to create a new workflow automation. What HR process should it handle?";
  return text;
}

const HELP_DISMISSED_KEY = "besthr_sara_help_pill_dismissed";

export function AIAssistantSheet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [assistantContext, setAssistantContext] = useState<AssistantContext>("general");
  const [onboardingContext, setOnboardingContext] = useState<OnboardingContext | null>(null);
  const [helpDismissed, setHelpDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(HELP_DISMISSED_KEY) === "1"; } catch { return false; }
  });

  const dismissHelp = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHelpDismissed(true);
    try { localStorage.setItem(HELP_DISMISSED_KEY, "1"); } catch {}
  };

  // Stable storage key scoped to tenant + user + assistant context.
  const baseStorageKey = `besthr_${getTenantSlug()}_${user?.id ?? "anon"}_sara`;
  const storageKey = assistantContext === "general" ? baseStorageKey : `${baseStorageKey}_${assistantContext}`;

  const [sessionId, setSessionId] = useState<string>(() => getStoredSessionId(storageKey));
  const [messages, setMessages] = useState<Message[]>(() => getStoredMessages(storageKey));
  const [input, setInput]         = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const bottomRef   = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const userAvatar: UserAvatarInfo | undefined = user
    ? {
        avatarUrl:   user.avatarUrl,
        avatarColor: user.avatarColor ?? "from-purple-500 to-indigo-500",
        initials:    user.initials ?? "?",
      }
    : undefined;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Persist messages to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}_msgs`, JSON.stringify(messages));
    } catch {}
  }, [messages, storageKey]);

  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 150);
  }, [open]);

  useEffect(() => {
    setSessionId(getStoredSessionId(storageKey));
    setMessages(getStoredMessages(storageKey));
    setInput("");
  }, [storageKey]);

  // Allow any page to open Sara by dispatching window.dispatchEvent(new CustomEvent("sara:open"))
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<SaraOpenEventDetail>).detail;
      if (detail?.context === "onboarding") {
        setAssistantContext("onboarding");
        setOnboardingContext(detail.onboarding || null);
      } else {
        setAssistantContext("general");
        setOnboardingContext(null);
      }
      setOpen(true);
    };
    window.addEventListener("sara:open", handler);
    return () => window.removeEventListener("sara:open", handler);
  }, []);

  const isOnboardingContext = assistantContext === "onboarding";
  const suggestions = isOnboardingContext
    ? getOnboardingSuggestions(t, onboardingContext)
    : GENERAL_SUGGESTIONS.map((item, index) => ({ ...item, label: t(`saraUi.${["headcount", "payroll", "employees", "overview"][index]}`) }));
  const sheetTitle = isOnboardingContext
    ? t("onboarding.saraTitle", "Sara AI асистент")
    : t("saraUi.title");
  const sheetSubtitle = isOnboardingContext
    ? t("onboarding.saraSubtitle", "BestHR паметен асистент")
    : t("saraUi.subtitle");
  const welcomeTitle = isOnboardingContext
    ? t("onboarding.saraWelcomeTitle", "Здраво, јас сум Сара!")
    : t("saraUi.welcomeTitle");
  const welcomeText = isOnboardingContext
    ? t("onboarding.saraWelcomeText", "Можам да ти помогнам со твојот онбординг процес.")
    : t("saraUi.welcomeText");
  const placeholder = isOnboardingContext
    ? t("onboarding.saraPlaceholder", "Прашај ја Сара нешто за твојот онбординг...")
    : t("saraUi.placeholder");
  const inputHint = isOnboardingContext
    ? t("onboarding.saraInputHint", "Enter за испраќање · Shift+Enter за нов ред")
    : t("saraUi.inputHint");

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const finalPrompt = expandCommand(trimmed);
      const userMsg: Message = {
        id:        `${Date.now()}-user`,
        role:      "user",
        content:   finalPrompt,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsLoading(true);

      const apiMessages: ApiMessage[] = [...messages, userMsg].map((m) => ({
        role:    m.role,
        content: m.content,
      }));

      try {
        const response = await apiSendAssistantMessage(apiMessages, sessionId);
        setMessages((prev) => [...prev, {
          id:           `${Date.now()}-assistant`,
          role:         "assistant",
          content:      response.message,
          actionsTaken: response.actionsTaken,
          timestamp:    new Date(),
        }]);
      } catch (err: any) {
        setMessages((prev) => [...prev, {
          id:        `${Date.now()}-error`,
          role:      "assistant",
          content:   err?.message?.toLowerCase().includes("not configured")
            ? (isOnboardingContext
                ? t("onboarding.saraNotConfigured", "AI асистентот не е конфигуриран. Контактирајте го администраторот.")
                : t("saraUi.notConfigured"))
            : (isOnboardingContext
                ? t("onboarding.chatError", "Извинете, нешто тргна наопаку. Обидете се повторно.")
                : t("saraUi.error")),
          timestamp: new Date(),
          isError:   true,
        }]);
      } finally {
        setIsLoading(false);
        setTimeout(() => textareaRef.current?.focus(), 50);
      }
    },
    [messages, isLoading, sessionId, isOnboardingContext, t],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  };

  return (
    <>
      {/* ── Floating Action Button ─────────────────────────────────────────── */}
      <div className={`fixed bottom-4 right-6 z-50 flex flex-col items-end gap-2 ${open ? "hidden" : ""}`}>
        {/* Help pill — shown until dismissed */}
        {!helpDismissed && (
          <div className="flex items-center gap-1 rounded-full border border-violet-200 bg-white/95 dark:bg-background/95 shadow-lg shadow-violet-100/50 dark:shadow-none backdrop-blur-sm px-3 py-1.5 text-xs text-violet-700 dark:text-violet-400 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <HelpCircle className="h-3 w-3 shrink-0" />
            <Link
              to="/sara-help"
              className="font-medium hover:underline whitespace-nowrap"
            >
              {t("saraUi.help")}
            </Link>
            <button
              onClick={dismissHelp}
              aria-label={t("saraUi.dismiss")}
              className="ml-1 rounded-full p-0.5 hover:bg-violet-100 dark:hover:bg-violet-900 transition-colors text-violet-400 hover:text-violet-700"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        )}

        {/* Main Sara button */}
        <button
          onClick={() => {
            setAssistantContext("general");
            setOnboardingContext(null);
            setOpen(true);
          }}
          aria-label={t("saraUi.open")}
          className="flex items-center gap-2.5 rounded-full shadow-2xl shadow-violet-700/30 bg-gradient-to-br from-violet-500 to-indigo-600 hover:from-violet-400 hover:to-indigo-500 active:scale-95 transition-all duration-200 text-white font-semibold text-sm pl-3 pr-5 py-2.5"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
            <img src="/besthr.png" alt="Sara" className="h-5 w-5 object-contain" />
          </div>
          <span>{t("saraUi.ask")}</span>
        </button>
      </div>

      {/* ── Slide-over Sheet ──────────────────────────────────────────────── */}
      <Sheet open={open} onOpenChange={setOpen}>
        {/* [&>button]:hidden hides the default Shadcn close button */}
        <SheetContent
          side="right"
          className="w-[420px] sm:w-[460px] p-0 flex flex-col gap-0 border-l border-border bg-background [&>button]:hidden"
        >
          {/* Header */}
          <SheetHeader className="flex-shrink-0 border-b px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl overflow-hidden bg-white shadow-md border border-border flex-shrink-0">
                  <img src="/besthr.png" alt="Sara" className="h-7 w-7 object-contain" />
                </div>
                <div>
                  <SheetTitle className="text-sm font-semibold leading-none">{sheetTitle}</SheetTitle>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{sheetSubtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setMessages([]);
                      setInput("");
                      try {
                        localStorage.removeItem(`${storageKey}_msgs`);
                        localStorage.removeItem(`${storageKey}_sid`);
                      } catch {}
                    }}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    title={t("saraUi.clear")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  title={t("saraUi.help")}
                >
                  <Link to="/sara-help" onClick={() => setOpen(false)}>
                    <HelpCircle className="h-3.5 w-3.5" />
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setOpen(false); navigate("/ai-assistant"); }}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  title="Open full screen"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          {/* Messages */}
          <ScrollArea className="flex-1">
            <div className="space-y-5 px-5 py-5">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl overflow-hidden bg-white shadow-lg border border-border">
                    <img src="/besthr.png" alt="Sara" className="h-10 w-10 object-contain" />
                  </div>
                  <p className="text-sm font-semibold mb-1">{welcomeTitle}</p>
                  <p className="text-xs text-muted-foreground mb-6 max-w-[280px]">
                    {welcomeText}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s.label}
                        onClick={() => sendMessage(s.prompt)}
                        disabled={isLoading}
                        className="rounded-full border bg-card px-3 py-1.5 text-xs transition-colors hover:border-primary/30 hover:bg-accent disabled:opacity-50"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {!isOnboardingContext && (
                    <div className="mt-6 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Zap className="h-2.5 w-2.5" />
                      <span>Type / for quick commands</span>
                    </div>
                  )}
                </div>
              ) : (
                messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} userAvatar={userAvatar} />
                ))
              )}
              {isLoading && <TypingIndicator />}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="flex-shrink-0 border-t px-4 py-3">
            <div className="flex items-end gap-2">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                placeholder={placeholder}
                disabled={isLoading}
                rows={1}
                className="min-h-[40px] max-h-28 resize-none py-2 pr-3 text-sm"
                style={{ height: "40px" }}
              />
              <Button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                size="icon"
                className="h-[40px] w-[40px] flex-shrink-0 bg-gradient-to-br from-violet-500 to-indigo-600 shadow-md hover:from-violet-600 hover:to-indigo-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-1.5 text-center text-[10px] text-muted-foreground">
              {inputHint}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
